/**
 * AII-3 (K45) — AI intake prefill confidence gate (pure policy).
 *
 * AI intake extraction carries an overall `confidence` (0..1). Confidence was
 * previously display-only: a low-confidence extraction could still silently
 * prefill advisor-facing Create Ticket fields as if authoritative. This policy
 * gates the PREFILL on that overall confidence:
 *
 *   - high   (>= 0.8): auto-prefill allowed (advisor still reviews + submits)
 *   - medium (>= 0.5): NO auto-prefill — deliberate advisor Apply, with a
 *                      visible "review before submitting" caution
 *   - low    (<  0.5): NO auto-prefill — explicit advisor Apply only, with a
 *                      "review required" warning
 *   - unknown (NaN/null/undefined/non-finite): treated as LOW — FAIL SAFE
 *
 * This is the OVERALL extraction confidence, NOT the matcher per-job
 * `match_confidence` (K29): deterministic synonym/catalog matches carry
 * `match_confidence:'none'` yet resolve to a real catalog row, so they must
 * NOT be suppressed here. This module never touches matcher output.
 *
 * Pure + unit-tested. No React, no I/O.
 */

export const AI_INTAKE_PREFILL_HIGH_THRESHOLD = 0.8;
export const AI_INTAKE_PREFILL_MEDIUM_THRESHOLD = 0.5;

export type AiIntakePrefillMode = 'auto' | 'review' | 'manual';
export type AiIntakeConfidenceLabel = 'High' | 'Medium' | 'Low' | 'Unknown';

function isValidConfidence(c: number | null | undefined): c is number {
  return typeof c === 'number' && Number.isFinite(c);
}

/**
 * Prefill mode for an overall extraction confidence.
 * Unknown/invalid confidence fails safe to 'manual' (never 'auto').
 */
export function getAiIntakePrefillMode(
  confidence: number | null | undefined,
): AiIntakePrefillMode {
  if (!isValidConfidence(confidence)) return 'manual'; // fail safe
  if (confidence >= AI_INTAKE_PREFILL_HIGH_THRESHOLD) return 'auto';
  if (confidence >= AI_INTAKE_PREFILL_MEDIUM_THRESHOLD) return 'review';
  return 'manual';
}

/**
 * True ONLY when the extraction may auto-prefill authoritative ticket fields
 * without a deliberate advisor click. High confidence only; fail-safe false
 * for medium / low / unknown.
 */
export function canAutoPrefillFromExtraction(
  confidence: number | null | undefined,
): boolean {
  return getAiIntakePrefillMode(confidence) === 'auto';
}

/** Human label for the confidence tier. */
export function getAiIntakeConfidenceLabel(
  confidence: number | null | undefined,
): AiIntakeConfidenceLabel {
  if (!isValidConfidence(confidence)) return 'Unknown';
  if (confidence >= AI_INTAKE_PREFILL_HIGH_THRESHOLD) return 'High';
  if (confidence >= AI_INTAKE_PREFILL_MEDIUM_THRESHOLD) return 'Medium';
  return 'Low';
}

/**
 * Advisor-facing warning for non-high confidence. Returns null for high
 * confidence (no warning needed). Wording never implies the AI is verified.
 */
export function getAiIntakeConfidenceWarning(
  confidence: number | null | undefined,
): string | null {
  const mode = getAiIntakePrefillMode(confidence);
  if (mode === 'auto') return null;
  if (mode === 'review') {
    return 'Medium AI confidence — review the extracted fields before submitting.';
  }
  // low or unknown
  return isValidConfidence(confidence)
    ? 'Review required before applying — AI confidence is low. Verify every field.'
    : 'Review required before applying — AI confidence is unknown. Verify every field.';
}
